import React, { useState, useEffect } from 'react';
import { CheckCircle, X, ClipboardList, Download, ShieldAlert, ChevronDown, ChevronRight, Paperclip, Trash2, Upload } from 'lucide-react';
import { authStorage } from '../../utils/storage';

const csvEscape = (value) => {
  if (value === null || value === undefined) return '';
  const str = String(value);
  return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

const pctPaying = (a) => {
  const total = Number(a.total_members);
  const paying = Number(a.paying_dues);
  if (!Number.isFinite(total) || total <= 0 || a.paying_dues === null || a.paying_dues === undefined || !Number.isFinite(paying)) return null;
  return Math.round((paying / total) * 100);
};

const Dash = () => <span className="ma-dash">—</span>;

const fmtBytes = (n) => {
  if (n === null || n === undefined) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

// Per-audit expandable detail: editable notes + reference attachments.
const AuditDetailRow = ({ audit, colSpan, onNotesSaved }) => {
  const token = authStorage.getToken();
  const [notes, setNotes] = useState(audit.notes || '');
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);
  const [attachments, setAttachments] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [detailError, setDetailError] = useState('');

  const fetchAttachments = async () => {
    try {
      const res = await fetch(`/api/membership-audits/${audit.id}/attachments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setAttachments(await res.json());
    } catch {
      setDetailError('Failed to load attachments.');
    }
  };

  useEffect(() => {
    fetchAttachments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audit.id]);

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    setDetailError('');
    setNotesSaved(false);
    try {
      const res = await fetch(`/api/membership-audits/${audit.id}/notes`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes })
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setNotesSaved(true);
      if (onNotesSaved) onNotesSaved(audit.id, notes);
    } catch {
      setDetailError('Failed to save notes.');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    setDetailError('');
    try {
      const form = new FormData();
      files.forEach((f) => form.append('files', f));
      const res = await fetch(`/api/membership-audits/${audit.id}/attachments`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || `HTTP ${res.status}`);
      }
      fetchAttachments();
    } catch (err) {
      setDetailError(err.message || 'Upload failed.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDownload = async (att) => {
    try {
      const res = await fetch(`/api/membership-audits/attachments/${att.id}/download`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.original_name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setDetailError('Download failed.');
    }
  };

  const handleDelete = async (att) => {
    if (!window.confirm(`Remove attachment "${att.original_name}"?`)) return;
    try {
      const res = await fetch(`/api/membership-audits/attachments/${att.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      fetchAttachments();
    } catch {
      setDetailError('Failed to remove attachment.');
    }
  };

  return (
    <tr>
      <td colSpan={colSpan} className="ma-detail-cell">
        <div className="ma-detail-card">
          <div>
            <div className="ma-detail-label">Notes for this audit</div>
            <textarea
              className="ma-notes-input"
              value={notes}
              onChange={(e) => { setNotes(e.target.value); setNotesSaved(false); }}
              rows={4}
              placeholder="Add notes about this audit…"
            />
            <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
              <button className="btn btn-primary btn-sm" onClick={handleSaveNotes} disabled={savingNotes} style={{ fontSize: 12, padding: '5px 14px' }}>
                {savingNotes ? 'Saving…' : 'Save Notes'}
              </button>
              {notesSaved && <span style={{ fontSize: 12, color: 'var(--color-success, #15803d)', fontWeight: 600 }}>✓ Saved</span>}
            </div>
          </div>
          <div>
            <div className="ma-detail-label">Attachments <small>· reference files for oddities</small></div>
            {attachments === null ? (
              <div style={{ fontSize: 12.5, color: 'var(--fg-3)', marginBottom: 8 }}>Loading…</div>
            ) : attachments.length === 0 ? (
              <div style={{ fontSize: 12.5, color: 'var(--fg-3)', marginBottom: 8 }}>No attachments yet.</div>
            ) : (
              <ul style={{ listStyle: 'none', margin: '0 0 10px', padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {attachments.map((att) => (
                  <li key={att.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                    <Paperclip size={12} style={{ color: 'var(--fg-3)', flexShrink: 0 }} />
                    <button
                      onClick={() => handleDownload(att)}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--accent, #2563eb)', fontSize: 12.5, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260 }}
                      title={`Download ${att.original_name}`}
                    >
                      {att.original_name}
                    </button>
                    <span style={{ color: 'var(--fg-3)', flexShrink: 0 }}>{fmtBytes(att.file_size)}</span>
                    <button
                      onClick={() => handleDelete(att)}
                      style={{ background: 'none', border: 'none', padding: 2, cursor: 'pointer', color: 'var(--fg-3)', flexShrink: 0 }}
                      title="Remove attachment"
                    >
                      <Trash2 size={12} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <label className="ma-attach-add" style={{ cursor: uploading ? 'wait' : 'pointer' }}>
              <Upload size={13} />
              {uploading ? 'Uploading…' : 'Add attachment'}
              <input type="file" multiple onChange={handleUpload} disabled={uploading} style={{ display: 'none' }} />
            </label>
          </div>
        </div>
        {detailError && <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--color-danger, #b91c1c)' }}>{detailError}</div>}
      </td>
    </tr>
  );
};

const MembershipAuditModal = ({ unitId, onClose, localLabel, decert, onDecertChange }) => {
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [signingOffId, setSigningOffId] = useState(null);
  const [isDecert, setIsDecert] = useState(Boolean(decert));
  const [savingDecert, setSavingDecert] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const showDecertToggle = decert !== undefined;
  const COL_SPAN = 16;

  const fetchAudits = async () => {
    setLoading(true);
    try {
      const token = authStorage.getToken();
      const res = await fetch(`/api/locals/${unitId}/membership-audits`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setAudits(await res.json());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAudits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitId]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleSignOff = async (auditId) => {
    if (!auditId) return;
    setSigningOffId(auditId);
    try {
      const token = authStorage.getToken();
      const res = await fetch(`/api/membership-audits/${auditId}/sign-off`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) fetchAudits();
    } catch {
      alert('Failed to sign off audit');
    } finally {
      setSigningOffId(null);
    }
  };

  function fmtDate(v) {
    if (!v) return '—';
    try { return new Date(v).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return String(v); }
  }

  function fmtDateTime(v) {
    if (!v) return '—';
    try { return new Date(v).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }); }
    catch { return String(v); }
  }

  const handleToggleDecert = async () => {
    setSavingDecert(true);
    try {
      const token = authStorage.getToken();
      const res = await fetch(`/api/locals/${unitId}/decert`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ decert: !isDecert })
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setIsDecert(!isDecert);
      if (onDecertChange) onDecertChange();
    } catch {
      alert('Failed to update decert status');
    } finally {
      setSavingDecert(false);
    }
  };

  const handleNotesSaved = (auditId, notes) => {
    setAudits((prev) => prev.map((a) => (a.id === auditId ? { ...a, notes } : a)));
  };

  const handleExportCsv = () => {
    const headers = [
      'Audit Date', 'Total Members', 'Paying Dues', '% Paying', 'Opt Outs', 'Refusals',
      'No Card No Dues', 'Dues w/ No Card', 'Card No Dues', 'Dues w/ Card',
      'Phone #s on File', 'Emails on File', 'Signed Off By', 'Notes', 'Recorded'
    ];
    const lines = [headers.join(',')];
    audits.forEach((a) => {
      const pct = pctPaying(a);
      lines.push([
        a.audit_date ? new Date(a.audit_date).toLocaleDateString() : '',
        a.total_members, a.paying_dues, pct !== null ? `${pct}%` : '',
        a.opt_outs, a.refusals,
        a.no_card_no_dues, a.dues_no_card, a.card_no_dues, a.dues_with_card,
        a.phones_on_file, a.emails_on_file,
        a.audited_by_name || '',
        a.notes || '',
        a.created_at ? new Date(a.created_at).toLocaleString() : ''
      ].map(csvEscape).join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    const nameSlug = String(localLabel || unitId)
      .replace(/[^\w-]+/g, '_')   // spaces/punctuation -> underscore
      .replace(/^_+|_+$/g, '')     // trim leading/trailing underscores
      .slice(0, 60) || String(unitId);
    anchor.download = `audit_history_${nameSlug}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  const signedCount = audits.filter(a => a.audited_by_user_id).length;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
    >
      <div
        className="ma-hist-modal"
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--bg-surface)', borderRadius: 'var(--radius-lg)',
          width: '100%', maxWidth: 1280, maxHeight: '86vh',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
      >
        <style>{`
          .ma-hist-modal .tbl th,
          .ma-hist-modal .tbl td { padding-top: 10px; padding-bottom: 10px; }
          .ma-hist-modal .tbl th:first-child,
          .ma-hist-modal .tbl td:first-child { padding-left: 18px; }
          .ma-hist-modal .tbl th:last-child,
          .ma-hist-modal .tbl td:last-child { padding-right: 18px; }
          /* One quiet visual weight for the header row: small caps, single
             baseline, numeric headers right-aligned over their columns. */
          .ma-hist-modal .tbl thead th {
            padding-top: 10px; padding-bottom: 10px;
            font-size: 10px !important; font-weight: 600;
            letter-spacing: .06em; color: var(--fg-3);
            white-space: normal; line-height: 1.25; vertical-align: bottom;
            text-transform: uppercase;
            border-bottom: 1px solid var(--border-subtle);
          }
          .ma-hist-modal .tbl thead th.num,
          .ma-hist-modal .tbl td.num {
            text-align: right;
            font-variant-numeric: tabular-nums;
          }
          .ma-hist-modal .tbl tbody tr.ma-audit-row { cursor: pointer; }
          .ma-hist-modal .tbl tbody tr.ma-audit-row:hover td { background: var(--bg-subtle, #f7f8fa); }
          .ma-hist-modal .tbl tbody tr.ma-audit-row.open td { background: var(--bg-accent-soft, #eef4ff); }
          .ma-hist-modal .ma-dash { color: var(--fg-3); opacity: .45; }
          /* Ghost header actions — the content is the hero, not the buttons */
          .ma-hist-modal .ma-ghost-btn {
            display: inline-flex; align-items: center; gap: 6px;
            font-size: 12px; font-weight: 600; padding: 6px 12px;
            border-radius: 7px; cursor: pointer;
            background: transparent; color: var(--fg-2);
            border: 1px solid var(--border-subtle, #d6dbe3);
          }
          .ma-hist-modal .ma-ghost-btn:hover:not(:disabled) { border-color: var(--fg-3); color: var(--fg-1); }
          .ma-hist-modal .ma-ghost-btn:disabled { opacity: .45; cursor: default; }
          .ma-hist-modal .ma-ghost-btn.decert-on {
            background: #f3eefe; border-color: #7c5cd6; color: #5b3fb5;
          }
          /* Expanded detail: a contained card, not floating fields */
          .ma-hist-modal .ma-detail-cell {
            background: var(--bg-subtle, #f7f8fa);
            border-top: none;
            padding: 14px 18px 18px !important;
          }
          .ma-hist-modal .ma-detail-card {
            background: var(--bg-surface, #fff);
            border: 1px solid var(--border-subtle, #e3e6eb);
            border-left: 3px solid var(--accent, #2563eb);
            border-radius: 8px;
            padding: 16px 18px;
            display: grid; grid-template-columns: 1.2fr 1fr; gap: 24px;
          }
          @media (max-width: 760px) { .ma-hist-modal .ma-detail-card { grid-template-columns: 1fr; } }
          .ma-hist-modal .ma-detail-label {
            font-size: 10px; font-weight: 700; text-transform: uppercase;
            letter-spacing: .07em; color: var(--fg-3); margin-bottom: 8px;
          }
          .ma-hist-modal .ma-detail-label small { font-weight: 500; text-transform: none; letter-spacing: 0; }
          .ma-hist-modal .ma-notes-input {
            width: 100%; box-sizing: border-box; font-size: 13px; padding: 9px 11px;
            border-radius: 7px; border: 1px solid var(--border-subtle, #d6dbe3);
            resize: vertical; font-family: inherit; background: var(--bg-surface, #fff);
            color: var(--fg-1); min-height: 84px;
          }
          .ma-hist-modal .ma-notes-input:focus { outline: 2px solid var(--accent, #2563eb); outline-offset: -1px; }
          .ma-hist-modal .ma-attach-add {
            display: inline-flex; align-items: center; gap: 7px;
            font-size: 12.5px; font-weight: 600; color: var(--accent, #2563eb);
            border: 1px dashed var(--accent, #2563eb); border-radius: 7px;
            padding: 7px 12px; cursor: pointer; background: transparent;
          }
          .ma-hist-modal .ma-attach-add:hover { background: var(--bg-accent-soft, #eef4ff); }
        `}</style>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '20px 24px', borderBottom: '1px solid var(--border-subtle)', flexShrink: 0
        }}>
          <ClipboardList size={16} style={{ color: 'var(--accent)' }} />
          <div>
            <div style={{ fontWeight: 600, fontSize: 15, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              Membership Audit History
              {isDecert && <span className="audit-pill decert" style={{ fontSize: 11 }}>Decert</span>}
            </div>
            <div style={{ fontSize: 12, color: 'var(--fg-3)' }}>
              {localLabel ? `${localLabel} · ` : ''}{loading ? 'Loading…' : `${audits.length} ${audits.length === 1 ? 'record' : 'records'} · ${signedCount} signed off`}
            </div>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            {showDecertToggle && (
              <button
                className={`ma-ghost-btn${isDecert ? ' decert-on' : ''}`}
                onClick={handleToggleDecert}
                disabled={savingDecert}
                title={isDecert ? 'Clear the decert flag for this local' : 'Mark this local as decertified (excluded from overdue tracking)'}
              >
                <ShieldAlert size={13} />
                {savingDecert ? 'Saving…' : isDecert ? 'Decert · on' : 'Mark Decert'}
              </button>
            )}
            <button className="ma-ghost-btn" onClick={handleExportCsv} disabled={loading || audits.length === 0}>
              <Download size={13} /> Export CSV
            </button>
            <button
              onClick={onClose}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--fg-3)', padding: 4 }}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '6px 0 14px' }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--fg-3)', fontSize: 13.5 }}>Loading…</div>
          ) : audits.length === 0 ? (
            <div style={{ padding: '60px 20px', textAlign: 'center', color: 'var(--fg-3)' }}>
              <ClipboardList size={32} style={{ marginBottom: 10, opacity: 0.3 }} />
              <p style={{ margin: 0, fontSize: 13.5 }}>No audit records found</p>
            </div>
          ) : (
            <div className="table-wrap" style={{ overflowX: 'auto' }}>
              <table className="tbl">
                <thead>
                  <tr>
                    <th style={{ width: 28 }} />
                    <th>Audit Date</th>
                    <th className="num">Total</th>
                    <th className="num">Paying</th>
                    <th className="num">% Paying</th>
                    <th className="num">Opt Out</th>
                    <th className="num">Refusal</th>
                    <th className="num">No Card / No Dues</th>
                    <th className="num">Dues / No Card</th>
                    <th className="num">Card / No Dues</th>
                    <th className="num">Dues w/ Card</th>
                    <th className="num">Phone #s</th>
                    <th className="num">Emails</th>
                    <th>Signed Off By</th>
                    <th>Recorded</th>
                    <th style={{ width: 100 }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {audits.map(a => {
                    const isSigned = !!a.audited_by_user_id;
                    const isExpanded = expandedId === a.id;
                    const pct = pctPaying(a);
                    const extras = (a.attachment_count || 0) + (a.notes ? 1 : 0);
                    return (
                      <React.Fragment key={a.id}>
                        <tr
                          className={`ma-audit-row${isExpanded ? ' open' : ''}`}
                          onClick={() => setExpandedId(isExpanded ? null : a.id)}
                          title="Click to view notes and attachments"
                        >
                          <td style={{ color: 'var(--fg-3)', paddingRight: 0 }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                              {extras > 0 && <Paperclip size={11} style={{ opacity: a.attachment_count ? 1 : 0.35 }} />}
                            </span>
                          </td>
                          <td style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{fmtDate(a.audit_date)}</td>
                          <td className="num">{a.total_members ?? <Dash />}</td>
                          <td className="num">{a.paying_dues ?? <Dash />}</td>
                          <td className="num" style={{ fontWeight: 600, color: 'var(--accent)' }}>{pct !== null ? `${pct}%` : <Dash />}</td>
                          <td className="num">{a.opt_outs ?? <Dash />}</td>
                          <td className="num">{a.refusals ?? <Dash />}</td>
                          <td className="num">{a.no_card_no_dues ?? <Dash />}</td>
                          <td className="num">{a.dues_no_card ?? <Dash />}</td>
                          <td className="num">{a.card_no_dues ?? <Dash />}</td>
                          <td className="num">{a.dues_with_card ?? <Dash />}</td>
                          <td className="num">{a.phones_on_file ?? <Dash />}</td>
                          <td className="num">{a.emails_on_file ?? <Dash />}</td>
                          <td style={{ color: 'var(--fg-2)', fontSize: 13 }}>
                            {a.audited_by_name || (isSigned ? 'Signed' : <Dash />)}
                          </td>
                          <td style={{ color: 'var(--fg-3)', fontSize: 12.5, whiteSpace: 'nowrap' }}>
                            {fmtDateTime(a.created_at)}
                          </td>
                          <td onClick={(e) => e.stopPropagation()}>
                            {isSigned ? (
                              <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: 5,
                                fontSize: 12, color: 'var(--color-success)', fontWeight: 500
                              }}>
                                <CheckCircle size={13} /> Signed
                              </span>
                            ) : (
                              <button
                                className="btn btn-primary btn-sm"
                                onClick={() => handleSignOff(a.id)}
                                disabled={signingOffId === a.id}
                                style={{ fontSize: 12, padding: '4px 10px' }}
                              >
                                <CheckCircle size={12} />
                                {signingOffId === a.id ? 'Signing…' : 'Sign'}
                              </button>
                            )}
                          </td>
                        </tr>
                        {isExpanded && (
                          <AuditDetailRow audit={a} colSpan={COL_SPAN} onNotesSaved={handleNotesSaved} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MembershipAuditModal;
